import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt, ShieldAlert } from 'lucide-react';
import './Disclaimer.css';

const SECTIONS = [
  {
    title: 'Not a Financial Service',
    body: `XpenseMate is a bill-splitting coordination tool. It is not a bank, payment processor, money transmitter, or financial institution of any kind. XpenseMate does not hold, move, transmit, or guarantee any funds at any time.`,
  },
  {
    title: 'Payments Are Between Users',
    body: `All payments facilitated through XpenseMate occur directly between users via independent third-party services (Venmo, Cash App, PayPal, UPI, and others). XpenseMate provides deep links to those services as a convenience only. Completion of any payment is solely between the paying user and the recipient through those services, subject to their own terms of service.`,
  },
  {
    title: 'No Payment Verification',
    body: `XpenseMate cannot verify, confirm, or guarantee that any payment has actually been made or received. Payment confirmations within the app rely entirely on voluntary self-reporting by users and manual confirmation by event organizers. A "confirmed" status in the app does not constitute proof of payment.`,
  },
  {
    title: 'Accuracy of Calculations',
    body: `While XpenseMate calculates expense splits based on the data entered by users, it makes no warranty regarding the accuracy of those calculations. Users are solely responsible for verifying all amounts before making or requesting payments. Rounding differences, input errors, and misclassified items are the responsibility of the users who submitted them.`,
  },
  {
    title: 'User Responsibility',
    body: `Users are solely responsible for: (a) verifying that payment amounts are correct before paying; (b) ensuring that their Venmo, Cash App, PayPal, or other handles are accurate; (c) resolving any payment disputes directly with other users; (d) complying with all applicable laws and the terms of third-party payment services they use.`,
  },
  {
    title: 'No Liability for Disputes',
    body: `XpenseMate and its owner(s) are not responsible for any disputes, unpaid amounts, overpayments, underpayments, fraudulent activity, or disagreements between users. Any financial dispute must be resolved directly between the parties involved, outside of this application.`,
  },
  {
    title: 'Third-Party Services',
    body: `XpenseMate is not affiliated with, endorsed by, or in partnership with Venmo, Cash App, PayPal, Google Pay, UPI, or any other payment service. References to those services are for user convenience only. XpenseMate is not liable for the availability, reliability, security, or terms of any third-party service.`,
  },
  {
    title: 'Data Storage',
    body: `Expense data including names, item descriptions, amounts, and payment handles entered into XpenseMate are stored in Google Firebase to provide the service. No financial account numbers, bank credentials, or sensitive payment credentials are collected or stored by XpenseMate. Users should avoid entering sensitive personal information beyond what is necessary to split expenses.`,
  },
  {
    title: 'Limitation of Liability',
    body: `To the fullest extent permitted by applicable law, XpenseMate and its owner(s) shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages — including but not limited to financial loss, loss of data, or loss of business — arising out of or related to your use of this application, even if advised of the possibility of such damages.`,
  },
  {
    title: 'No Warranties',
    body: `XpenseMate is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that the service will be uninterrupted, error-free, or free of viruses or other harmful components.`,
  },
  {
    title: 'Acceptance of Terms',
    body: `By using XpenseMate — including creating events, submitting expenses, or viewing settlement data — you acknowledge that you have read and understood this disclaimer and agree to its terms. If you do not agree, you should not use this application.`,
  },
];

export default function Disclaimer() {
  const navigate = useNavigate();

  return (
    <div className="dl-page">
      <header className="dl-header">
        <button className="dl-back" onClick={() => navigate(-1)}>← Back</button>
        <div className="dl-logo">
          <div className="dl-logo-icon"><Receipt size={16} color="white" /></div>
          <span className="dl-logo-text">XpenseMate</span>
        </div>
      </header>

      <main className="dl-main">
        <div className="dl-hero">
          <div className="dl-shield">
            <ShieldAlert size={28} />
          </div>
          <h1 className="dl-title">Disclaimer &amp; Terms of Use</h1>
          <p className="dl-subtitle">
            Please read this carefully before using XpenseMate.
            Last updated: April 2026.
          </p>
        </div>

        <div className="dl-notice">
          <strong>Summary:</strong> XpenseMate is a free coordination tool — it helps groups track who owes what and links to payment apps. It does not process payments, hold money, or verify that payments were made. All financial responsibility remains with the users.
        </div>

        <div className="dl-sections">
          {SECTIONS.map((s, i) => (
            <div key={i} className="dl-section">
              <h2 className="dl-section-title">
                <span className="dl-section-num">{i + 1}</span>
                {s.title}
              </h2>
              <p className="dl-section-body">{s.body}</p>
            </div>
          ))}
        </div>

        <div className="dl-footer-note">
          <p>
            Questions? Contact the app owner at{' '}
            <a href="mailto:mohapatra.shyam@gmail.com" className="dl-link">
              mohapatra.shyam@gmail.com
            </a>
          </p>
          <p className="dl-copyright">© 2026 XpenseMate. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
}
